import { LockOutlined } from '@ant-design/icons';
import { history, request, useLocation } from '@umijs/max';
import { Alert, Button, Card, Form, Input, Space, Spin } from 'antd';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clearSystemAccess, discardSystemAccess, isSystemPath, readSystemAccess, readSavedSystemAccess, revokeSystemPage, saveSystemAccess, SYSTEM_ACCESS_EXPIRED } from '@/utils/systemAccess';

interface PageEntry { id: string; entry: string; loginToken: string }

const SystemAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const protectedPage = isSystemPath(location.pathname);
  const [revision, setRevision] = useState(0);
  // Pagination, filters, anchors and repeated clicks on the same menu item do
  // not enter a different management page or extend the verification deadline.
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  const entry = JSON.stringify([pathname, revision]);
  const currentEntry = useRef(entry);
  currentEntry.current = entry;
  const pageRef = useRef<PageEntry | null>(null);
  const submissionRef = useRef<PageEntry | null>(null);
  const [verifiedEntry, setVerifiedEntry] = useState('');
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    const expire = (event: Event) => {
      const page = pageRef.current;
      if (page) revokeSystemPage(page.id, page.loginToken);
      setVerifiedEntry('');
      setError((event as CustomEvent<string>).detail || '');
      setRevision(value => value + 1);
    };
    const hide = () => {
      discardSystemAccess(true);
      setVerifiedEntry('');
    };
    const show = (event: PageTransitionEvent) => { if (event.persisted) setRevision(value => value + 1); };
    window.addEventListener(SYSTEM_ACCESS_EXPIRED, expire);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', show);
    return () => {
      window.removeEventListener(SYSTEM_ACCESS_EXPIRED, expire);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', show);
    };
  }, []);

  useLayoutEffect(() => {
    let active = true;
    let opened: PageEntry | null = null;
    const saved = readSavedSystemAccess();
    const resumable = saved?.pathname === pathname ? saved : null;
    discardSystemAccess(!protectedPage || !!resumable);
    pageRef.current = null;
    setVerifiedEntry('');
    setSubmitting(false);
    form.resetFields();
    if (!protectedPage) { setChecking(false); return; }
    setChecking(true);
    const loginToken = localStorage.getItem('token') || '';
    const enter = async () => {
      if (resumable) {
        const response = await request('/api/v1/system-access/status', {
          headers: { 'X-System-Verification': resumable.token }, skipErrorHandler: true,
        });
        if (!active || localStorage.getItem('token') !== loginToken) return;
        if (response?.success && response.data?.verified && response.data.pageId === resumable.pageId &&
            Date.parse(response.data.expiresAt) > Date.now()) {
          opened = { id: resumable.pageId!, entry, loginToken };
          pageRef.current = opened;
          saveSystemAccess({ token: resumable.token, expiresAt: response.data.expiresAt }, { pageId: opened.id, pathname });
          setVerifiedEntry(entry);
          return;
        }
        discardSystemAccess();
      } else if (saved) {
        revokeSystemPage(saved.pageId!, saved.loginToken);
      }
      const response = await request('/api/v1/system-access/page', { method: 'POST', skipErrorHandler: true });
      if (!response?.success || !response.data?.pageId) throw new Error('无法创建页面验证，请刷新重试');
      opened = { id: response.data.pageId, entry, loginToken };
      if (!active || localStorage.getItem('token') !== loginToken) { revokeSystemPage(opened.id, loginToken); return; }
      pageRef.current = opened;
    };
    void enter()
      .catch(err => {
        if (active) setError(err.response?.data?.message || err.message || '无法检查页面验证，请刷新重试');
      })
      .finally(() => { if (active) setChecking(false); });
    // The document may be refreshing. Only an actual new management page or
    // explicit invalidation revokes the saved proof; its deadline never slides.
    return () => { active = false; pageRef.current = null; };
  }, [entry, pathname, protectedPage, form]);

  useEffect(() => {
    if (verifiedEntry !== entry) return;
    const session = readSystemAccess();
    if (!session) { clearSystemAccess(); return; }
    const timer = window.setTimeout(() => clearSystemAccess(), Math.max(0, Date.parse(session.expiresAt) - Date.now()));
    // A browser dialog or tab switch does not leave this page. On return, only
    // clear a proof that actually expired or no longer belongs to this login.
    const recheck = () => { if (!readSystemAccess()) clearSystemAccess(); };
    const visibility = () => { if (!document.hidden) recheck(); };
    window.addEventListener('focus', recheck);
    window.addEventListener('storage', recheck);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', recheck);
      window.removeEventListener('storage', recheck);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [verifiedEntry, entry]);

  const verify = async ({ password }: { password: string }) => {
    const initialPage = pageRef.current;
    if (!initialPage || initialPage.entry !== entry) { setError('无法创建页面验证，请刷新重试'); return; }
    let page = initialPage;
    // Block duplicate submits synchronously, before React disables the button.
    if (submissionRef.current === page) return;
    submissionRef.current = page;
    setSubmitting(true);
    setError('');
    const isCurrent = () => currentEntry.current === page.entry && pageRef.current === page &&
      localStorage.getItem('token') === page.loginToken;
    const submit = () => request('/api/v1/system-access/verify', {
      method: 'POST', data: { password, pageId: page.id }, skipErrorHandler: true,
    });
    try {
      let response;
      try {
        response = await submit();
      } catch (err: any) {
        if (!isCurrent()) return;
        if (err.response?.data?.code !== 'SYSTEM_VERIFICATION_PAGE_EXPIRED') throw err;
        // An idle form or another tab can retire this challenge. Retry once
        // with a fresh page, still requiring this explicitly submitted password.
        const opened = await request('/api/v1/system-access/page', { method: 'POST', skipErrorHandler: true });
        if (!isCurrent()) {
          if (opened?.data?.pageId) revokeSystemPage(opened.data.pageId, page.loginToken);
          return;
        }
        if (!opened?.success || !opened.data?.pageId) throw new Error('无法创建页面验证，请刷新重试');
        page = { ...page, id: opened.data.pageId };
        pageRef.current = page;
        submissionRef.current = page;
        response = await submit();
      }
      if (!isCurrent()) return;
      if (!response?.success || !response.data?.token) throw new Error(response?.message || '密码长度错误');
      saveSystemAccess(response.data, { pageId: page.id, pathname });
      form.resetFields();
      setVerifiedEntry(entry);
    } catch (err: any) {
      if (!isCurrent()) return;
      form.resetFields();
      setError(err.response?.data?.message || err.response?.data?.error || err.message || '密码长度错误');
    } finally {
      if (submissionRef.current === page) submissionRef.current = null;
      if (pageRef.current === page) setSubmitting(false);
    }
  };

  if (!protectedPage) return <>{children}</>;
  if (checking) return <div role="status" style={{ padding: 80, textAlign: 'center' }}><Spin /><div style={{ marginTop: 12 }}>检查系统管理访问权限</div></div>;
  if (verifiedEntry === entry && readSystemAccess()) {
    return <><div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
      <Button icon={<LockOutlined />} onClick={() => clearSystemAccess()}>锁定系统管理</Button>
    </div>{children}</>;
  }
  return (
    <Card title={<Space><LockOutlined />系统管理二次验证</Space>} style={{ maxWidth: 460, margin: '64px auto' }}>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 20 }} />}
      <Form form={form} layout="vertical" onFinish={verify} preserve={false}>
        <Form.Item name="password" label="二次密码" rules={[{ required: true, message: '密码长度错误' }]}>
          <Input.Password autoComplete="off" autoFocus placeholder="请输入系统管理二次密码" disabled={submitting} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={submitting}>验证并进入</Button>
          <Button onClick={() => history.push('/welcome')}>返回首页</Button>
        </Space>
      </Form>
    </Card>
  );
};

export default SystemAccessGate;
